// Generic 8-state surface frame (Law 8 / 18H §1.4): empty · loading-skeleton
// (never a spinner) · error · partial · success · offline · unauthorized ·
// rate-limited. `partial` is intentionally NOT one of the frame states — a
// page in "partial" is structurally "ready" with one panel/sub-fetch that
// failed; render that with its own inline AlertBanner next to the data that
// did load (see the trust console pages), not as a full-surface takeover.
//
// Usage:
//   const [state, setState] = useState<LoadState>('loading');
//   <StateFrame state={state} skeleton={<MySkeleton/>} onRetry={load}
//     empty={data.length === 0}
//     emptyIcon="inbox" emptyTitle="Nothing yet" emptyBody="...">
//     {...ready content...}
//   </StateFrame>
import * as React from 'react';
import { WifiOff, Lock, Clock3, AlertTriangle } from 'lucide-react';
import { EmptyState } from './EmptyState';
import { ApiError } from '@/lib/supabase';

export type LoadState = 'loading' | 'error' | 'offline' | 'unauthorized' | 'rate-limited' | 'ready';

// Classify a thrown apiRequest() error into one of the three "the request
// itself failed" states. Anything else (a real 4xx/5xx business error) is
// the generic 'error' state.
export function classifyApiError(err: unknown): 'error' | 'offline' | 'unauthorized' | 'rate-limited' {
  if (err instanceof ApiError) {
    if (err.status === 0) return 'offline';
    if (err.status === 401 || err.status === 403) return 'unauthorized';
    if (err.status === 429) return 'rate-limited';
  }
  return 'error';
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong';
}

interface StatusPanelProps {
  icon: React.ElementType;
  color: string;
  bg: string;
  title: string;
  body: string;
  onRetry?: () => void;
}
function StatusPanel({ icon: IconCmp, color, bg, title, body, onRetry }: StatusPanelProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '52px 40px', textAlign: 'center' }}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        <IconCmp size={24} color={color} />
      </div>
      <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.5, maxWidth: 360, marginBottom: onRetry ? 18 : 0 }}>{body}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{ padding: '9px 18px', borderRadius: 8, background: 'var(--accent)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

export interface StateFrameProps {
  state: LoadState;
  skeleton: React.ReactNode;
  onRetry?: () => void;
  errorMessage?: string | null;
  // Empty-state (rendered only when state === 'ready' && empty === true)
  empty?: boolean;
  emptyIcon?: string;
  emptyTitle?: string;
  emptyBody?: string;
  emptyAction?: () => void;
  emptyActionLabel?: string;
  children: React.ReactNode;
}

export function StateFrame({
  state, skeleton, onRetry, errorMessage, empty, emptyIcon = 'inbox', emptyTitle = 'Nothing here yet',
  emptyBody = 'No data to show.', emptyAction, emptyActionLabel, children,
}: StateFrameProps) {
  if (state === 'loading') return <>{skeleton}</>;
  if (state === 'offline') {
    return (
      <StatusPanel
        icon={WifiOff} color="var(--text-muted)" bg="var(--bg-muted)"
        title="You're offline" body="Couldn't reach the license server. Check your connection and try again."
        onRetry={onRetry}
      />
    );
  }
  if (state === 'unauthorized') {
    return (
      <StatusPanel
        icon={Lock} color="#92400E" bg="#FEF3C7"
        title="You don't have access to this" body="Your role doesn't grant access to this surface. Ask a super admin if you believe this is wrong."
      />
    );
  }
  if (state === 'rate-limited') {
    return (
      <StatusPanel
        icon={Clock3} color="#92400E" bg="#FEF3C7"
        title="Slow down a moment" body="Too many requests in a short window. Wait a few seconds and retry."
        onRetry={onRetry}
      />
    );
  }
  if (state === 'error') {
    return (
      <StatusPanel
        icon={AlertTriangle} color="var(--status-expired)" bg="#FEE2E2"
        title="Couldn't load this" body={errorMessage || 'An unexpected error occurred.'}
        onRetry={onRetry}
      />
    );
  }
  if (empty) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} body={emptyBody} action={emptyAction} actionLabel={emptyActionLabel} />;
  }
  return <>{children}</>;
}

export { errMessage };
export default StateFrame;
